import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Dialog } from '../../components/ui/Dialog.js';
import { Field, Select, TextArea, TextInput } from '../../components/ui/Field.js';
import { outboundApi, usersApi } from '../../lib/api.js';
import { formatMinutes } from '../../lib/format.js';
import type { SnoozeInput } from '../../lib/api.js';
import { SNOOZE_PRESETS } from './snooze.js';

export interface SnoozeDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (input: SnoozeInput) => void;
  /** How many alarms the snooze applies to (for the bulk case). */
  count?: number;
  pending?: boolean;
}

/**
 * Snooze with presets, a custom end time and an optional note. `POST
 * /alarms/:id/snooze` takes `{ until, note }`; severity and team are separate
 * actions, so they are not offered here.
 */
export function SnoozeDialog({ open, onClose, onConfirm, count = 1, pending }: SnoozeDialogProps) {
  const [minutes, setMinutes] = useState<number>(60);
  const [custom, setCustom] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    setMinutes(60);
    setCustom('');
    setNote('');
  }, [open]);

  const submit = (): void => {
    const input: SnoozeInput = custom ? { until: new Date(custom).toISOString() } : { minutes };
    if (note.trim()) input.note = note.trim();
    onConfirm(input);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title={count > 1 ? `Snooze ${count} alarms` : 'Snooze alarm'}
      description="Notifications pause until the snooze expires; the alarm then returns to the state it left."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={pending}>
            Snooze
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <fieldset>
          <legend className="pb-1 text-[var(--text-sm)] font-medium text-[var(--text-2)]">
            For how long
          </legend>
          <div className="flex flex-wrap gap-1.5">
            {SNOOZE_PRESETS.map((preset) => (
              <Button
                key={preset}
                size="sm"
                variant={!custom && minutes === preset ? 'primary' : 'secondary'}
                onClick={() => {
                  setMinutes(preset);
                  setCustom('');
                }}
              >
                {formatMinutes(preset)}
              </Button>
            ))}
          </div>
        </fieldset>

        <Field label="Or until a specific time" hint="Local time; overrides the preset above.">
          {({ id }) => (
            <TextInput
              id={id}
              type="datetime-local"
              value={custom}
              onChange={(event) => setCustom(event.target.value)}
            />
          )}
        </Field>

        <Field label="Note">
          {({ id }) => (
            <TextArea
              id={id}
              rows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Waiting on the vendor"
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

export interface AssignDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (userId: string | null, label: string) => void;
  count?: number;
  pending?: boolean;
  currentAssigneeId?: string | null;
}

/** Assignee picker, with "unassign" as an explicit choice. */
export function AssignDialog({
  open,
  onClose,
  onConfirm,
  count = 1,
  pending,
  currentAssigneeId,
}: AssignDialogProps) {
  const [userId, setUserId] = useState(currentAssigneeId ?? '');
  const users = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.listAll(),
    enabled: open,
    retry: false,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (open) setUserId(currentAssigneeId ?? '');
  }, [open, currentAssigneeId]);

  const selected = (users.data ?? []).find((user) => user.id === userId);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title={count > 1 ? `Assign ${count} alarms` : 'Assign alarm'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={pending}
            onClick={() => onConfirm(userId || null, selected?.display_name ?? '')}
          >
            {userId ? 'Assign' : 'Unassign'}
          </Button>
        </>
      }
    >
      <Field label="Assignee">
        {({ id }) => (
          <Select id={id} value={userId} onChange={(event) => setUserId(event.target.value)}>
            <option value="">Unassigned</option>
            {(users.data ?? []).map((user) => (
              <option key={user.id} value={user.id}>
                {user.display_name} · {user.email}
              </option>
            ))}
          </Select>
        )}
      </Field>
    </Dialog>
  );
}

export interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (outboundId: string) => void;
  mode: 'share' | 'ticket';
  pending?: boolean;
}

/** Choose an outbound integration to share the alarm to, or raise a ticket in. */
export function ShareDialog({ open, onClose, onConfirm, mode, pending }: ShareDialogProps) {
  const [outboundId, setOutboundId] = useState('');
  const outbound = useQuery({
    queryKey: ['outbound', 'all'],
    queryFn: () => outboundApi.listAll(),
    enabled: open,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const TICKET_TYPES = ['servicedeskplus', 'jira', 'servicenow', 'pagerduty'];
  const options = (outbound.data ?? []).filter((item) =>
    mode === 'ticket' ? TICKET_TYPES.includes(item.type) : true,
  );

  useEffect(() => {
    if (open) setOutboundId(options[0]?.id ?? '');
    // `options` is derived from the query; re-selecting on every render would fight the user.
  }, [open, outbound.data]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title={mode === 'ticket' ? 'Raise a ticket' : 'Share this alarm'}
      description={
        mode === 'ticket'
          ? 'Creates an incident in the selected ITSM system and links it to this alarm.'
          : 'Sends the alarm to an outbound integration (Teams, Slack, webhook, email).'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!outboundId}
            loading={pending}
            onClick={() => onConfirm(outboundId)}
          >
            {mode === 'ticket' ? 'Create ticket' : 'Share'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field
          label="Outbound integration"
          hint={options.length === 0 ? 'No outbound integrations are configured yet.' : undefined}
        >
          {({ id }) => (
            <Select
              id={id}
              value={outboundId}
              onChange={(event) => setOutboundId(event.target.value)}
            >
              <option value="">Select…</option>
              {options.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} · {item.type}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
    </Dialog>
  );
}
